import { useState, useEffect, useCallback } from 'react'
import { openCheckout, initPaddle } from '@/lib/paddle'
import { toast } from 'sonner'
import { useAuth } from '@/hooks/useAuth'

interface UsePaddleCheckoutReturn {
  openCheckout: (priceId: string) => void
  isReady: boolean
  isLoading: boolean
  error: Error | null
}

/**
 * React hook for Paddle checkout functionality
 * Initializes Paddle on mount and provides checkout function
 * Automatically includes organization ID in customData for webhook identification
 */
export function usePaddleCheckout(): UsePaddleCheckoutReturn {
  const [isReady, setIsReady] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)
  const { user, organization } = useAuth()

  useEffect(() => {
    const initializePaddle = async () => {
      try {
        setIsLoading(true)
        setError(null)

        // Initialize Paddle instance
        await initPaddle()

        setIsReady(true)
      } catch (err) {
        const error = err instanceof Error ? err : new Error('Failed to initialize Paddle')
        setError(error)
        toast.error('Failed to initialize payment system', {
          description: error.message,
        })
      } finally {
        setIsLoading(false)
      }
    }

    initializePaddle()
  }, [])

  const handleOpenCheckout = useCallback(
    (priceId: string) => {
      if (!priceId) {
        toast.error('Invalid plan selected', {
          description: 'Please select a valid plan to upgrade.',
        })
        return
      }

      if (!isReady) {
        toast.error('Payment system not ready', {
          description: 'Please wait for the payment system to initialize.',
        })
        return
      }

      if (!user) {
        toast.error('User not found', {
          description: 'Please log in to continue.',
        })
        return
      }

      if (!organization?.id) {
        toast.error('Organization not found', {
          description: 'Unable to identify your organization. Please refresh the page.',
        })
        return
      }

      try {
        openCheckout(priceId, {
          customData: {
            organization_id: organization?.id,
            organization_name: organization?.name,
          },
          customerEmail: user.email,
          organizationName: organization?.name,
        })
      } catch (err) {
        const error = err instanceof Error ? err : new Error('Failed to open checkout')
        toast.error('Failed to open checkout', {
          description: error.message,
        })
      }
    },
    [isReady, user, organization]
  )

  return {
    openCheckout: handleOpenCheckout,
    isReady,
    isLoading,
    error,
  }
}

